import { ArrowUpRight, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocale, useLocalePath, useTranslation } from "@thyra/landing-i18n";
import type { BlogPostSummary } from "@/src/lib/ghost";
import { blogPostPath } from "@/src/lib/blog-paths";
import { formatPostDate, ghostSrcSet } from "@/src/lib/blog-format";

/**
 * `index` is the card's position on the page: the first three load eagerly
 * (they are above the fold) and the first also gets high fetch priority.
 * `headingLevel` keeps the outline valid: 2 under a page <h1>, 3 under an <h2>.
 */
export function PostCard({
  post,
  index = 99,
  headingLevel = 2,
}: {
  post: BlogPostSummary;
  index?: number;
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const { t } = useTranslation("blog");
  const locale = useLocale();
  const lp = useLocalePath();
  return (
    // A post in another language than the page (English fallback) is marked
    // so screen readers and translators treat it as such.
    <article
      lang={post.lang !== locale ? post.lang : undefined}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-card-hover)]"
    >
      <div className="aspect-[16/9] w-full overflow-hidden bg-[var(--color-primary-muted)]">
        {post.featureImage ? (
          <img
            src={post.featureImage}
            alt={post.featureImageAlt ?? ""}
            width={600}
            height={315}
            srcSet={ghostSrcSet(post.featureImage, [600, 1000])}
            sizes="(min-width: 1024px) 384px, (min-width: 640px) 50vw, 100vw"
            loading={index < 3 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : undefined}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-primary/60">
            <Newspaper className="h-10 w-10" aria-hidden />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        {post.primaryTag && (
          <span className="text-xs font-semibold uppercase tracking-wide text-primary">
            {post.primaryTag.name}
          </span>
        )}
        <Heading className="text-lg font-semibold leading-snug text-foreground">
          {/* Stretched link: the whole card is clickable, one tab stop. */}
          <Link
            to={lp(blogPostPath(post.slug))}
            className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-primary/50 focus-visible:after:rounded-2xl"
          >
            {post.title}
          </Link>
        </Heading>
        {post.excerpt && (
          <p className="line-clamp-3 text-sm leading-6 text-muted-foreground">
            {post.excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 pt-2 text-xs text-muted-foreground">
          <span>
            <time dateTime={post.publishedAt}>
              {formatPostDate(post.publishedAt, locale)}
            </time>
            {" · "}
            {t("card.readTime", { count: post.readingTime })}
          </span>
          <ArrowUpRight
            className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        </div>
      </div>
    </article>
  );
}

export function PostCardSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border bg-card"
      aria-hidden
    >
      <div className="aspect-[16/9] animate-pulse bg-muted" />
      <div className="space-y-3 p-5">
        <div className="h-3 w-16 animate-pulse rounded bg-muted" />
        <div className="h-5 w-4/5 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
